// Task 14 (WU-2.3) — /admin/directed-notes: supervisor sent-list page.
// AnnouncementsAdminPage scaffold (page/pageHeader/table shell). Filters
// (state select + admin-only "All senders" checkbox) drive
// directedNotesApi.sent directly — the server does the actual filtering —
// so the fetch effect's deps are exactly [state, all].
//
// Retract confirms via window.confirm before calling the API (the test
// stubs window.confirm — see DirectedNotesSentPage.test.tsx).
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWsClient } from "@/lib/ws-provider";
import { ApiError } from "@/lib/api-client";
import { showApiError } from "@/lib/error-toast";
import { toast } from "@/components/Toaster";
import { directedNoteState, type DirectedNoteRecord, type DirectedNoteState } from "@capture/shared";
import { directedNotesApi } from "../api/client";
import { ComposeNoteDialog } from "../components/ComposeNoteDialog";
import styles from "../directed-notes.module.css";

type StateFilter = "" | DirectedNoteState;

function statusLabel(n: DirectedNoteRecord): string {
  const state = directedNoteState(n);
  if (state === "acknowledged") {
    return `Acknowledged ${n.acknowledgedAt ? new Date(n.acknowledgedAt).toLocaleString() : ""}`;
  }
  if (state === "retracted") return "Retracted";
  return "Awaiting ack";
}

export function DirectedNotesSentPage() {
  const { user } = useAuth();
  const wsClient = useWsClient();
  const isAdmin = user?.permissions?.includes("admin") ?? false;

  const [notes, setNotes] = useState<DirectedNoteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<StateFilter>("");
  const [all, setAll] = useState(false);
  const [composing, setComposing] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      // sent() only sets the query param when it's truthy, so an empty
      // state ("" = "All") or all=false is already a no-op downstream.
      const res = await directedNotesApi.sent({ state, all: isAdmin && all });
      setNotes(res.notes);
    } finally {
      setLoading(false);
    }
  }, [state, all, isAdmin]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Live ack refresh — a supervisor watching this page sees an operator's
  // acknowledgement land without a manual refresh.
  useEffect(() => {
    if (!wsClient) return;
    return wsClient.onPayload("directed_note_acked", () => {
      void refetch();
    });
  }, [wsClient, refetch]);

  const retract = async (id: string) => {
    if (!window.confirm("Retract this note? The operator will no longer see it.")) {
      return;
    }
    try {
      await directedNotesApi.retract(id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("Already acknowledged — cannot retract");
      } else {
        showApiError(err);
      }
    } finally {
      await refetch();
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1>Directed notes</h1>
        <button type="button" onClick={() => setComposing(true)}>
          New note
        </button>
      </header>

      <div className={styles.filters}>
        <label className={styles.filterField}>
          State
          <select value={state} onChange={(e) => setState(e.target.value as StateFilter)}>
            <option value="">All</option>
            <option value="active">Awaiting</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="retracted">Retracted</option>
          </select>
        </label>
        {isAdmin && (
          <label className={styles.checkboxField}>
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
            All senders
          </label>
        )}
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : notes.length === 0 ? (
        <p className={styles.empty}>No sent notes.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>To</th>
              <th>Note</th>
              <th>Document</th>
              <th>Sent</th>
              <th>Status</th>
              <th>Reply</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {notes.map((n) => {
              const isActive = directedNoteState(n) === "active";
              return (
                <tr key={n.id}>
                  <td>{n.toUserName ?? n.toUserId}</td>
                  <td>{n.body}</td>
                  <td>{n.context?.filename ?? "—"}</td>
                  <td>{new Date(n.createdAt).toLocaleString()}</td>
                  <td>{statusLabel(n)}</td>
                  <td>{n.reply ?? "—"}</td>
                  <td>
                    {isActive && (
                      <button type="button" onClick={() => void retract(n.id)}>
                        Retract
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {composing && (
        <ComposeNoteDialog
          onClose={() => setComposing(false)}
          onSent={() => {
            setComposing(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
}
